// Microphone recording for speech-to-text, with a hard maximum duration.
//
// The cap is the participant-side half of the anti-runaway guard: a recording
// stops itself after MAX_RECORDING_MS and ships what it has, so a participant
// who walks away can't leave the mic open and run up the owner's bill. The
// server enforces its own size backstop independently.

export const MAX_RECORDING_MS = 10 * 60 * 1000; // 10 minutes

export class MicPermissionError extends Error {
  constructor() {
    super("Microphone access was blocked.");
    this.name = "MicPermissionError";
  }
}

export class VoiceRecorder {
  private rec: MediaRecorder | null = null;
  private stream: MediaStream | null = null;
  private chunks: Blob[] = [];
  private autoTimer: number | null = null;

  // Start recording. Resolves once the mic is live. Throws MicPermissionError
  // if the participant denies (or the browser blocks) microphone access.
  async start(onAutoStop?: () => void, maxMs: number = MAX_RECORDING_MS): Promise<void> {
    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch {
      throw new MicPermissionError();
    }
    this.stream = stream;
    this.chunks = [];
    this.rec = new MediaRecorder(stream);
    this.rec.ondataavailable = (e: BlobEvent) => {
      if (e.data && e.data.size > 0) this.chunks.push(e.data);
    };
    this.rec.start();
    this.autoTimer = window.setTimeout(() => {
      this.autoTimer = null;
      if (onAutoStop) onAutoStop();
    }, maxMs);
  }

  // Stop recording and resolve with the captured audio blob.
  stop(): Promise<Blob> {
    return new Promise<Blob>((resolve) => {
      if (this.autoTimer !== null) {
        window.clearTimeout(this.autoTimer);
        this.autoTimer = null;
      }
      const rec = this.rec;
      if (!rec || rec.state === "inactive") {
        this.cleanup();
        resolve(new Blob(this.chunks, { type: "audio/webm" }));
        return;
      }
      rec.onstop = () => {
        const type = rec.mimeType || "audio/webm";
        this.cleanup();
        resolve(new Blob(this.chunks, { type }));
      };
      rec.stop();
    });
  }

  private cleanup(): void {
    if (this.stream) {
      this.stream.getTracks().forEach((tr) => tr.stop());
      this.stream = null;
    }
    this.rec = null;
  }
}
